/* eslint-disable @typescript-eslint/no-explicit-any */
import { isUnchangedEmailField } from '../_schemas/HcpSchemas/ContactSection.validation';

type AnyValue = any;

// These keys alone are NOT enough to make a new item valid
const METADATA_KEYS = new Set([
	'isPrimary',
	'isPrimaryRelationship_V',
	'primaryCda_V',
	'fieldName',
	'vid_V'
]);

// Strict check for empty values (clickin the add button "+" and not filling in any fields)
const isEmpty = (val: any) => {
	if (val === null || val === undefined || val === '') return true;
	if (val instanceof Date) return isNaN(val.getTime());
	if (Array.isArray(val) && val.length === 0) return true;
	if (typeof val === 'object' && Object.keys(val).length === 0) return true;

	return false;
};

type MultiPrimaryRow = { id?: string; name?: string };

const sameLocalSpecArray = (
	current: AnyValue[] | undefined,
	defaults: AnyValue[] | undefined
) => {
	const currentIds = ((current ?? []) as MultiPrimaryRow[])
		.map(i => (i?.id ?? '').toString().trim())
		.filter(Boolean);
	const defaultIds = ((defaults ?? []) as MultiPrimaryRow[])
		.map(i => (i?.id ?? '').toString().trim())
		.filter(Boolean);

	if (currentIds.length !== defaultIds.length) return false;
	return currentIds.every((id, i) => id === defaultIds[i]);
};

export function getDirtyValuesHybrid<T extends Record<string, AnyValue>>(
	dirtyFields: Record<string, AnyValue>,
	currentValues: T,
	defaultValues?: AnyValue
): Partial<T> {
	const getBackendId = (item: any) => item?.vid_V;

	// Arrays: Return a filtered array (no empty slots, no objects)
	if (Array.isArray(dirtyFields) && Array.isArray(currentValues)) {
		const result: AnyValue[] = [];

		dirtyFields.forEach((dirtyItem, index) => {
			if (!dirtyItem) return;

			const currentItem = currentValues[index];

			if (!currentItem) return;

			const itemChanges = getDirtyValuesHybrid(dirtyItem, currentItem);

			if (Object.keys(itemChanges).length > 0) {
				const vid = getBackendId(currentItem);

				// --- GHOST CHECK --- (e.g. in Creation Mode a Medical Degrees field has isPrimary=true/false, but no ID/name values, thus it's invalid)
				// If NO vid_V (New Item), check for Real Data
				if (!vid) {
					const hasRealData = Object.entries(itemChanges).some(
						([key, value]) => {
							if (METADATA_KEYS.has(key)) return false;

							// Ignore empty values - checks strings, nulls, but allows Dates, IDs, etc
							if (isEmpty(value)) return false;

							if (typeof value === 'object' && !Array.isArray(value)) {
								return Object.values(value).some(v => !isEmpty(v));
							}

							return true;
						}
					);

					// If no real data found (e.g. user added a row but typed nothing), discard it
					if (!hasRealData) return;
				}

				// --- INJECTIONS ---
				// Item is valid, so we ensure context is attached
				// Backend ID - existing items
				if (vid && !itemChanges.vid_V) {
					itemChanges.vid_V = vid;
				}

				// 2. Context injection
				if (currentItem?.fieldName && !itemChanges.fieldName) {
					itemChanges.fieldName = currentItem.fieldName;
				}
				if (currentItem?.isPrimary !== undefined && !itemChanges.isPrimary) {
					itemChanges.isPrimary = currentItem.isPrimary;
				}
				if (
					currentItem?.primaryCda_V !== undefined &&
					!itemChanges.primaryCda_V
				) {
					itemChanges.primaryCda_V = currentItem.primaryCda_V;
				}
				if (
					currentItem?.isPrimaryRelationship_V !== undefined &&
					!itemChanges.isPrimaryRelationship_V
				) {
					itemChanges.isPrimaryRelationship_V =
						currentItem.isPrimaryRelationship_V;
				}

				result.push(itemChanges);
			}
		});

		return result as any;
	}

	// Objects
	const result: AnyValue = {};

	if (!dirtyFields || typeof dirtyFields !== 'object') return result;

	Object.keys(dirtyFields).forEach(key => {
		const dirtyVal = dirtyFields[key];
		const currentVal = currentValues?.[key];

		if (dirtyVal === true) {
			// Same idea as the allSpecLocal_VId check below, for email fields.
			if (isUnchangedEmailField(key, currentVal, defaultValues?.[key])) {
				return;
			}

			// Primitive field changed (string, boolean, number)
			result[key] = currentVal;
		} else if (typeof dirtyVal === 'object' && dirtyVal !== null) {
			// Nested object changed
			let nested;
			if (key === 'allSpecLocal_VId') {
				const filtered = ((currentVal ?? []) as MultiPrimaryRow[]).filter(
					i => i?.id && String(i.id).trim() !== ''
				);
				if (sameLocalSpecArray(filtered, defaultValues?.[key])) {
					return;
				}
				nested = filtered;
			} else {
				nested = getDirtyValuesHybrid(dirtyVal, currentVal);
			}

			if (Object.keys(nested).length > 0) {
				// Inject ID if nested object is dirty and has a backend ID
				const vid = getBackendId(currentVal);
				if (!Array.isArray(nested) && vid && !nested.vid_V) {
					nested.vid_V = vid;
				}

				result[key] = nested;
			} else if (
				Array.isArray(dirtyVal) &&
				dirtyVal.length > 0 &&
				Array.isArray(currentVal) &&
				currentVal.length === 0
			) {
				// Array was dirty but is now empty (user removed all items).
				// Emit an empty array so consumers can detect the clear.
				result[key] = [];
			}
		}
	});

	return result;
}
